import { CertificationGoalContext } from '../../certification-goal-context';
import {reachGoal} from 'utils/metrica';
import {formatReferenceCopy} from 'utils/typography';
import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import {useForm, useFieldArray, FormProvider, useWatch} from "react-hook-form";

import Typography from "@mui/material/Typography";
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import Button from "@mui/material/Button";

import {COLORS} from 'config/constants';

import './auto.scss';

import {CertificationType} from "../../index";
import RadioTemplate from "../radio-template";
import CertificationCode from "./components/certification-code";
import {
  useDeclarations,
  useLazyDeclarationSummaryByOrderIdQuery,
  useLazyGetBrokerDataByCodeQuery,
  useSetCompletedByOrderIdMutation,
} from "../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";
import {useDemoCertificationAction} from 'components/cabinet/demo/steps/certification/use-certification';

import {isCalculationViewModeError, numberWithSpaces} from "../../../../../../../utils/helpers";
import cx from 'classnames';

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../../../calculation-read-only';
import {useLazyGetDeclarationTypesQuery} from "../../../../../../../api/declaration_types";
import {useLazyGetDocumentDurationsQuery} from "../../../../../../../api/document_durations";
import {
  useLazyGetDocumentTypesByCodeQuery,
  useLazyGetDocumentTypesQuery
} from "../../../../../../../api/document_types";
import {isNull} from "lodash";
import {BROKER_ACTIONS} from "../../../../../../../utils/constants";

export enum CERTIFICATION_AUTO_FORM {
  DECLARATION_TYPE = 'declaration_type',
  CERTIFICATION_SCHEME = 'certification_scheme',
  DOCUMENT_DURATION = 'document_duration',
  HAS_CERTIFICATE = 'has_certificate',
  HAS_BROKER = 'has_broker',
  DOCUMENT_TYPES = 'document_types',
  NO_NEED_CERTIFICATE = 'no_need_certificate',
  COST_TOTAL_MANUAL = 'cost_total_manual',
  ACTION = 'action',
}

export type CertificationAutoForm = {
  [CERTIFICATION_AUTO_FORM.DECLARATION_TYPE]: string
  [CERTIFICATION_AUTO_FORM.CERTIFICATION_SCHEME]: string,
  [CERTIFICATION_AUTO_FORM.DOCUMENT_DURATION]: string
  [CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE]: string,
  [CERTIFICATION_AUTO_FORM.HAS_BROKER]: string,
  [CERTIFICATION_AUTO_FORM.DOCUMENT_TYPES]: any[],
  [CERTIFICATION_AUTO_FORM.NO_NEED_CERTIFICATE]: string | boolean,
  [CERTIFICATION_AUTO_FORM.COST_TOTAL_MANUAL]: string,
  [CERTIFICATION_AUTO_FORM.ACTION]: string,
}

export type CertificationFormType = {
  codes: CertificationAutoForm[]
}

type Props = {
  isExpanded: boolean
  value?: number
  isActive: boolean
  handleChangeAccordion(value: CertificationType, isExpanded: boolean): void
  handleComplete(): void
  handleChange(): void
}

const CertificationAuto = ({ isExpanded, handleChangeAccordion, handleComplete, isActive, value }: Props) => {

  const { t } = useTranslation();


  const orderId = useGetCalcIdFromRoute()

  const [ triggerFetchSummaryDeclaration ] = useLazyDeclarationSummaryByOrderIdQuery();
  const [ documentTypesByCodeRequest ] = useLazyGetDocumentTypesByCodeQuery();

  const [ declarationTypesRequest ] = useLazyGetDeclarationTypesQuery()
  const [ documentDurationsRequest  ] = useLazyGetDocumentDurationsQuery()
  const [ documentTypesRequest ] = useLazyGetDocumentTypesQuery()

  const [ codes, setCodes ] = React.useState<Declaration[]>([]);

  const [ isMainButtonDisabled, setIsMainButtonDisabled ] = useState(true);
  const readOnly = useCalculationReadOnly()
  const demoPlayback = useDemoCertificationAction({
    disabled: isMainButtonDisabled,
  })

  const declarations = useDeclarations();
  const [ goalFields, setGoalFields ] = React.useState<Record<string, unknown>>({});
  const reportGoalFields = React.useCallback((fields: Record<string, unknown>) => {
    setGoalFields((previous) => ({ ...previous, ...fields }));
  }, []);

  const [ setCompleted ] = useSetCompletedByOrderIdMutation()

  const [ triggerGetBrokerDataByCode, { data: brokerData } ] = useLazyGetBrokerDataByCodeQuery();

  const methods = useForm<CertificationFormType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      codes: []
    },
  });

  useEffect(() => {
    declarationTypesRequest('');
    documentDurationsRequest('');
    documentTypesRequest('');
    triggerGetBrokerDataByCode(orderId);
  }, [ ])

  const { control, watch, reset, getValues } = methods;

  const { fields } = useFieldArray({
    control,
    // @ts-ignore
    name: "codes",
  });

  useEffect(() => {
    if (declarations?.length){
      let declarationsCodes: Declaration[] = [];
      declarations?.map((declaration) => {
        declarationsCodes = [ ...declarationsCodes, ...declaration.codes ];
      })
      setCodes(declarationsCodes);
    }
  }, [ declarations ]);

  useEffect(() => {

    const fetchDocs = async () => {

      if (!codes?.length) return;

      const responses = await Promise.all(
        codes.map(async (code) => {
          const result = await documentTypesByCodeRequest(code.code).unwrap();

          const docItems = result.map((doc: DocumentsByCodeItem) => {
            const brokerCenter = brokerData?.find((b) =>
              b.tech_reglament === doc.code &&
                  b.country_code === code.country_data.code &&
                  b.code === code.code
            );

            const brokerNoNeeded = isNull(brokerCenter?.has_certificate);

            return {
              ...doc,
              [CERTIFICATION_AUTO_FORM.HAS_BROKER]: brokerCenter && !brokerNoNeeded ? "true" : "",
              brokerCenter: brokerNoNeeded ? undefined : brokerCenter,
              [CERTIFICATION_AUTO_FORM.NO_NEED_CERTIFICATE]: brokerNoNeeded ? "true" : "false",
            };
          });

          const hasBrokerNoNeeded = docItems.some(
            (d) => d[CERTIFICATION_AUTO_FORM.NO_NEED_CERTIFICATE] === "true"
          );

          const brokerDataCurrent = brokerData?.find((b) =>
            b.code === code.code && b.country_code === code.country_data.code
          );

          const hasSkippedTheStep = brokerDataCurrent?.action === BROKER_ACTIONS.CERT_SKIP;

          const hasAnyBrokerCenter = result.some((doc: DocumentsByCodeItem) =>
            brokerData?.some((b) =>
              b.code === code.code &&
                    b.country_code === code.country_data.code &&
                    (b?.company?.id || isNull(b?.has_certificate))
            )
          );

          const costTotalManual = !brokerDataCurrent?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE]
              && brokerDataCurrent?.cost_total && !brokerDataCurrent?.company?.id;

          let action: string = '';

          if (hasSkippedTheStep) {
            action = BROKER_ACTIONS.CERT_SKIP;
          } else if (costTotalManual){
            action = BROKER_ACTIONS.CERT_MANUAL;
          } else if (brokerDataCurrent?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE]) {
            action = BROKER_ACTIONS.CERT_EXISTS;
          } else if (!result.length && brokerDataCurrent?.has_certificate === null) {
            action = BROKER_ACTIONS.CERT_FIND_MISSING_REGULATION;
          }

          return {
            [CERTIFICATION_AUTO_FORM.DOCUMENT_TYPES]: docItems,
            [CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE]: (() => {
              if (hasSkippedTheStep) return "null";
              if (costTotalManual) return "custom";
              if (hasAnyBrokerCenter || (!result.length && brokerDataCurrent?.has_certificate === null)) return "false";
              return brokerDataCurrent?.[CERTIFICATION_AUTO_FORM.HAS_CERTIFICATE] ? "true" : "";
            })(),
            [CERTIFICATION_AUTO_FORM.NO_NEED_CERTIFICATE]: !result.length,
            [CERTIFICATION_AUTO_FORM.COST_TOTAL_MANUAL]: costTotalManual ? brokerDataCurrent?.cost_total : undefined,
            [CERTIFICATION_AUTO_FORM.ACTION]: action
          };
        }));

      // @ts-ignore
      reset({ codes: responses });
    };

    fetchDocs();
  }, [ codes, brokerData, orderId ]);

  const watchFieldArray = useWatch({ name: "codes", control });

  useEffect(() => {
    const hasValid = watchFieldArray?.every(
      (item) =>
        item.has_certificate === "true" ||
            (item.has_certificate === "false" &&
                item.document_types?.every((document) => document.has_broker === "true"
                    || document.no_need_certificate === "true")) ||
          (item.has_certificate === "false" && item.no_need_certificate) ||
          item.has_certificate === "null" ||
          (item.has_certificate === "custom" && item.cost_total_manual)
    );

    setIsMainButtonDisabled(!hasValid);
  }, [ watchFieldArray, codes ]);

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index]
    };
  });

  const handleOpenAccordion = (isExpanded: boolean) => {
    handleChangeAccordion(CertificationType.AUTO, isExpanded);
  }

  const handleSaveCertification = async () => {
    const result = await setCompleted({
      orderId,
      certification: true
    })

    if (isCalculationViewModeError(result)) {
      return;
    }

    handleComplete();
    triggerFetchSummaryDeclaration(orderId);
  }

  return <div className='certification-auto'>
    <RadioTemplate
      isActive={isActive}
      title={<Typography variant='h6' sx={{
        color: COLORS.BLACK_DARK,
        fontSize: '20px',
        fontWeight: 700,
        lineHeight: '26px',
        letterSpacing: 0,
        paddingLeft: '16px',
        gap: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}>
        {formatReferenceCopy(t('dictionaries__text__certification__findOutTheCostOfCertification'))}
        <Typography component='span' variant='inherit' sx={{
          fontWeight: 400,
          flexShrink: 0,
          textAlign: 'right',
        }}>
          {value ? `${numberWithSpaces(value)} USD` : undefined}
        </Typography>
      </Typography>}
      value={CertificationType.AUTO}
      isExpanded={isExpanded}
      handleChangeAccordion={handleOpenAccordion}
    >
      <div className='certification-auto__container'>
        <CertificationGoalContext.Provider value={{ fields: goalFields, report: reportGoalFields }}>
          <FormProvider {...methods}>
            {controlledFields.map((item, index) => {
              return <CertificationCode
                index={index}
                key={index}
                code={codes[index]}
              />
            })}
          </FormProvider>
        </CertificationGoalContext.Provider>
        <Button
          {...demoPlayback.buttonProps}
          variant='contained'
          fullWidth
          className={cx({ 'read-only-action-button': readOnly })}
          startIcon={<CheckIcon />}
          endIcon={readOnlyButtonEndIcon(readOnly)}
          onClick={readOnly ? undefined : () => {
            reachGoal({
              goal: 'stoimost_sertifikacii',
              params: { codes: Object.values(goalFields).filter(Boolean) },
            });
            handleSaveCertification();
          }}
          disabled={readOnly || demoPlayback.disabled}
          title={readOnly ? t('calc__message__lockedByApplication') : undefined}
        >
          <Typography component='span' variant='inherit'>
            {formatReferenceCopy(t('dictionaries__text__certification__saveTheCostOfCertification'))}
          </Typography>
        </Button>
      </div>
    </RadioTemplate>
  </div>
}

export default CertificationAuto
